/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ILabelProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { ScoreFilter } from 'src/luis/projects/utterances/types/Filters';
import { QnaMakerMetadata } from 'src/qnaMaker/core/components/QnaMakerMetadata';
import { LogicalOperation } from 'src/qnaMaker/core/services/qnaMakerProjectSwaggers';
import { QnaMakerMetadataType } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { FiltersBladeSlider } from 'src/verticals/utterancesFilters/components/FiltersBladeSlider';

type QnaMakerChatTestBladeOptionsProps = {
    scoreFilter: ScoreFilter;
    includeShortAnswer: boolean;
    usePublishedVersion: boolean;
    metaData: QnaMakerMetadataType;
    filterOperation: LogicalOperation;
    onChangeIncludeShortAnswer: (newVal: boolean) => void;
    onChangeUsePublishedVersion: (newVal: boolean) => void;
    onChangeScoreFilter: (newScoreRange: ScoreFilter) => void;
    onChangeMetadata: (newMetadata: QnaMakerMetadataType) => void;
    onChangeFilterOperation: (newVal: LogicalOperation) => void;
    flattenMetadata: Set<string>;
};

const messages = defineMessages({
    score: { id: 'qna.qnaMakerChatTestBladeOptions.score', defaultMessage: 'Score' },
    metadata: { id: 'qna.qnaMakerChatTestBladeOptions.metadata', defaultMessage: 'Metadata' },
    orOperation: { id: 'qna.qnaMakerChatTestBladeOptions.orOperation', defaultMessage: 'OR' },
    operation: { id: 'qna.qnaMakerChatTestBladeOptions.operation', defaultMessage: 'Operation' },
    andOperation: { id: 'qna.qnaMakerChatTestBladeOptions.andOperation', defaultMessage: 'AND' },
    responseOptions: { id: 'qna.qnaMakerChatTestBladeOptions.responseOptions', defaultMessage: 'Response options' },
    scoreSliderMinAriaLabel: { id: 'qna.qnaMakerChatTestBladeOptions.scoreSliderMinAriaLabel', defaultMessage: 'minimum score' },
    scoreSliderMaxAriaLabel: { id: 'qna.qnaMakerChatTestBladeOptions.scoreSliderMaxAriaLabel', defaultMessage: 'maximum score' },
    showAdvancedOptions: { id: 'qna.qnaMakerChatTestBladeOptions.showAdvancedOptions', defaultMessage: 'Show advanced options' },
    hideAdvancedOptions: { id: 'qna.qnaMakerChatTestBladeOptions.hideAdvancedOptions', defaultMessage: 'Hide advanced options' },
    usePublishedVersion: { id: 'qna.qnaMakerChatTestBladeOptions.usePublishedVersion', defaultMessage: 'Use deployed knowledge base' },
    includeShortAnswer: { id: 'qna.qnaMakerChatTestBladeOptions.includeShortAnswer', defaultMessage: 'Include short answer response' },
    deployedVersionDescription: {
        id: 'qna.qnaMakerChatTestBladeOptions.deployedVersionDescription',
        defaultMessage: 'Deploy your project first to use this option'
    },
    shortAnswerCoachmark: {
        id: 'qna.qnaMakerChatTestBladeOptions.shortAnswerCoachmark',
        defaultMessage: 'Select checkbox to enable short-answers.'
    },
    learnMore: {
        id: 'qna.qnaMakerChatTestBladeOptions.learnMore',
        defaultMessage: 'Learn more'
    },
    deployCoachmark: {
        id: 'qna.qnaMakerChatTestBladeOptions.deployCoachmark',
        defaultMessage: 'Publish your knowledge base to test the published version.'
    },
    scoreDescription: {
        id: 'qna.qnaMakerChatTestBladeOptions.scoreDescription',
        defaultMessage: 'You can select a threshold score. Answers with confidence score below threshold will not be returned'
    },
    metadataDescription: {
        id: 'qna.qnaMakerChatTestBladeOptions.metadataDescription',
        defaultMessage:
            'You can select metadata values to filter responses. We will only consider QnAs with selected metadata to fetch response to user query.'
    }
});

const theme = getScopedTheme('QnaMakerChatTestBlade');
const { getClassName } = getStylist('QnaMakerChatTestBladeOptions');
const infoIconClassName = getClassName('infoIconClassName', { fontSize: '12px' });

const optionsContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `1px solid ${theme.defaultBorderColor}` }
});
const metadataContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { maxHeight: '150px', border: `1px solid ${theme.defaultBorderColor}` }
});
const flexLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({
    root: { display: 'flex', alignItems: 'flex-end', fontWeight: 'normal' }
});

export const QnaMakerChatTestBladeOptions = observer((props: QnaMakerChatTestBladeOptionsProps) => {
    const {
        metaData,
        scoreFilter,
        filterOperation,
        onChangeMetadata,
        includeShortAnswer,
        usePublishedVersion,
        onChangeScoreFilter,
        onChangeFilterOperation,
        onChangeIncludeShortAnswer,
        onChangeUsePublishedVersion,
        flattenMetadata
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const [hideAdvancedOptions, setHideAdvancedOptions] = React.useState(true);

    const { currentQnaMakerProjectStore } = rootStore;
    const isProjectDeployed = Boolean(currentQnaMakerProjectStore.lastPublishedDate);
    const operationOptions: IDropdownOption[] = [
        { key: 'OR', text: localize(messages.orOperation) },
        { key: 'AND', text: localize(messages.andOperation) }
    ];

    const renderLabel = (label: string, description: React.ReactNode, text: string, isDisabled: boolean = false) => (
        <Label aria-label={label} styles={flexLabelStyles}>
            <LuisText isDisabled={isDisabled}>{label}</LuisText>
            <CalloutButton isDarkMode={false} ariaLabel={text} content={description} iconClassName={infoIconClassName} />
        </Label>
    );

    return (
        <Stack tokens={{ childrenGap: 12, padding: '20px 15px' }} styles={optionsContainerStyles}>
            <LuisText fontWeight={500}>{localize(messages.responseOptions)}</LuisText>

            <Stack tokens={{ childrenGap: 4 }}>
                <Checkbox
                    checked={includeShortAnswer}
                    onChange={(_, isChecked) => onChangeIncludeShortAnswer(isChecked)}
                    onRenderLabel={() =>
                        renderLabel(
                            localize(messages.includeShortAnswer),
                            <LuisText>
                                {localize(messages.shortAnswerCoachmark)} &nbsp;
                                <ExternalLink href="https://aka.ms/qa-precise-answer">{localize(messages.learnMore)}</ExternalLink>
                            </LuisText>,
                            `${localize(messages.shortAnswerCoachmark)} ${localize(messages.learnMore)}`
                        )
                    }
                />
                <Checkbox
                    disabled={!isProjectDeployed}
                    checked={usePublishedVersion}
                    onChange={(_, isChecked) => onChangeUsePublishedVersion(isChecked)}
                    onRenderLabel={() =>
                        renderLabel(
                            localize(messages.usePublishedVersion),
                            !isProjectDeployed ? (
                                localize(messages.deployedVersionDescription)
                            ) : (
                                <LuisText>
                                    {localize(messages.deployCoachmark)} &nbsp;
                                    <ExternalLink href="https://aka.ms/qna-test-kb">{localize(messages.learnMore)}</ExternalLink>
                                </LuisText>
                            ),
                            !isProjectDeployed
                                ? localize(messages.deployedVersionDescription)
                                : `${localize(messages.deployCoachmark)} ${localize(messages.learnMore)}`,
                            !isProjectDeployed
                        )
                    }
                />
            </Stack>

            {!hideAdvancedOptions && [
                <Stack horizontal verticalAlign="end">
                    <LuisText aria-label={localize(messages.metadata)} fontWeight={500}>
                        {localize(messages.metadata)}
                    </LuisText>
                    <CalloutButton
                        isDarkMode={false}
                        content={<LuisText>{localize(messages.metadataDescription)}</LuisText>}
                        iconClassName={infoIconClassName}
                        ariaLabel={`Metadata, ${localize(messages.metadataDescription)}`}
                    />
                </Stack>,
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
                    <LuisText>{localize(messages.operation)}</LuisText>
                    <LuisDropdown
                        tabIndex={0}
                        aria-label={localize(messages.operation)}
                        options={operationOptions}
                        selectedKey={filterOperation}
                        isSearchEnabled={false}
                        onChange={(_, option) => onChangeFilterOperation(option.key as LogicalOperation)}
                    />
                </Stack>,
                <Stack styles={metadataContainerStyles}>
                    <QnaMakerMetadata
                        isInEditMode={false}
                        showDeleteOption
                        onChangeMetadata={onChangeMetadata}
                        metadata={metaData}
                        isAddActionDisabled={false}
                        areKeysShownInDropDown
                        flattenMetadata={flattenMetadata}
                    />
                </Stack>,
                <Stack horizontal verticalAlign="end">
                    <LuisText aria-label={localize(messages.score)} fontWeight={500}>
                        {localize(messages.score)}
                    </LuisText>
                    <CalloutButton
                        isDarkMode={false}
                        content={<LuisText>{localize(messages.scoreDescription)}</LuisText>}
                        iconClassName={infoIconClassName}
                        ariaLabel={`Score, ${localize(messages.scoreDescription)}`}
                    />
                </Stack>,
                <FiltersBladeSlider
                    disableMaxChange
                    score={scoreFilter}
                    minHandleAriaLabel={localize(messages.scoreSliderMinAriaLabel)}
                    maxHandleAriaLabel={localize(messages.scoreSliderMaxAriaLabel)}
                    onScoreChange={filterChange => onChangeScoreFilter(filterChange.args as ScoreFilter)}
                />
            ]}

            {!hideAdvancedOptions && (
                <Link onClick={() => setHideAdvancedOptions(!hideAdvancedOptions)}>
                    <span aria-label={localize(messages.hideAdvancedOptions)} role="button">
                        {localize(messages.hideAdvancedOptions)}
                    </span>
                </Link>
            )}

            {hideAdvancedOptions && (
                <Link onClick={() => setHideAdvancedOptions(!hideAdvancedOptions)}>
                    <span aria-label={localize(messages.showAdvancedOptions)} role="button" aria-haspopup>
                        {localize(messages.showAdvancedOptions)}
                    </span>
                </Link>
            )}
        </Stack>
    );
});
